import { useState, useCallback } from 'react'
import { importarPedidosBling, importarDetalhesBlingAtivos, importarDetalhesContatosBling, isBlingConnected } from '../utils/bling'
import { saveBlingHistorico, getBlingHistoricoMeta, clearBlingHistorico, saveBlingPedidosDetalhes, getBlingContatos, updateBlingContatosEnriquecidos, clearBlingDetalhes } from '../utils/storage'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Download, RefreshCw, Trash2, CheckCircle, AlertCircle, Database } from 'lucide-react'

export function ImportarBlingHistorico({ onImportado, compact = false }) {
  const [importando, setImportando] = useState(false)
  const [progresso,  setProgresso]  = useState(0)
  const [erro,       setErro]       = useState(null)
  const [meta,       setMeta]       = useState(() => getBlingHistoricoMeta())
  const [fase,       setFase]       = useState('')

  const blingOk = isBlingConnected()

  const handleImportar = useCallback(async () => {
    setImportando(true)
    setErro(null)
    setProgresso(0)
    setFase('lista')
    try {
      // Fase 1: importa a lista
      const pedidos = await importarPedidosBling(n => setProgresso(n))
      saveBlingHistorico(pedidos)

      // Fase 2: detalhes APENAS dos pedidos com prazo em 2026 (os que aparecem na Logística)
      // — evita buscar milhares de pedidos antigos desnecessariamente
      const IGNORAR  = new Set([12, 0])
      const ativos2026 = pedidos.filter(p =>
        !IGNORAR.has(p.situacao?.id) &&
        p.dataPrevista?.startsWith('2026')
      )

      if (ativos2026.length > 0) {
        setFase('detalhes')
        setProgresso(0)
        // NÃO limpar cache ANTES do fetch — se o token expirar ou a rede
        // cair no meio, o cache antigo seria perdido e a Logística ficaria
        // sem itens até o próximo import bem-sucedido. saveBlingPedidosDetalhes
        // já faz merge por id, então overwrite seletivo é suficiente.
        const ids = ativos2026.map(p => p.id)
        const detalhes = await importarDetalhesBlingAtivos(ids, (atual, total) => {
          setProgresso(Math.round((atual / total) * 100))
        })
        if (detalhes.length > 0) saveBlingPedidosDetalhes(detalhes)
        // Reporta falhas (401/500/etc) — antes eram engolidas silenciosamente.
        if (detalhes.falhas?.length > 0) {
          setErro(`Importação parcial: ${detalhes.length}/${ids.length} detalhes OK. ${detalhes.falhas.length} falharam (${detalhes.falhas.slice(0, 3).map(f => f.status).join(', ')}${detalhes.falhas.length > 3 ? '…' : ''}). Rode o import de novo pra tentar os que faltaram.`)
        }
      }

      // Fase 3: endereço dos contatos dos pedidos 2026
      // — pula contatos que já têm endereço salvo
      const contatosExistentes = new Map(getBlingContatos().map(c => [String(c.id), c]))
      const contatoIds = [
        ...new Set(ativos2026.map(p => p.contato?.id).filter(Boolean).map(String)),
      ].filter(id => !contatosExistentes.get(id)?.endereco?.geral?.endereco)

      if (contatoIds.length > 0) {
        setFase('contatos')
        setProgresso(0)
        const enriquecidos = await importarDetalhesContatosBling(contatoIds, (atual, total) => {
          setProgresso(Math.round((atual / total) * 100))
        })
        if (enriquecidos.length > 0) updateBlingContatosEnriquecidos(enriquecidos)
      }

      setMeta(getBlingHistoricoMeta())
      onImportado?.()
    } catch (e) {
      setErro(e.message)
    } finally {
      setImportando(false)
      setFase('')
    }
  }, [onImportado])

  const handleLimpar = useCallback(() => {
    clearBlingHistorico()
    setMeta(null)
    setErro(null)
    onImportado?.()
  }, [onImportado])

  if (importando) {
    return compact ? (
      <div className="flex items-center gap-2 px-3 py-2 bg-[#FFF9C2] border border-[#F5C200]/40 rounded-xl">
        <RefreshCw size={11} className="text-[#CC8800] animate-spin shrink-0" />
        <span className="text-xs font-semibold text-[#CC8800] flex-1">
          {fase === 'detalhes' ? `Itens 2026… ${progresso}%` : fase === 'contatos' ? `Endereços… ${progresso}%` : 'Importando pedidos…'}
        </span>
        {fase === 'lista' && <span className="text-[10px] text-slate-500">{progresso}</span>}
      </div>
    ) : (
      <div className="flex items-center gap-3 px-4 py-3 bg-[#FFF9C2] border border-[#F5C200]/40 rounded-xl text-sm">
        <RefreshCw size={14} className="text-[#CC8800] animate-spin shrink-0" />
        <span className="font-semibold text-[#CC8800]">
          {fase === 'detalhes' ? 'Buscando itens dos pedidos 2026…' : fase === 'contatos' ? 'Buscando endereços…' : 'Importando histórico…'}
        </span>
        <span className="text-slate-500 ml-auto">
          {fase === 'lista' ? `${progresso} pedidos` : `${progresso}%`}
        </span>
      </div>
    )
  }

  if (erro) {
    return compact ? (
      <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
        <AlertCircle size={11} className="text-red-500 shrink-0 mt-0.5" />
        <p className="text-[10px] text-red-700 flex-1">{erro}</p>
        <button onClick={handleImportar} className="text-[10px] text-red-500 hover:underline shrink-0">Tentar</button>
      </div>
    ) : (
      <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm">
        <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
        <p className="text-red-700 flex-1 text-xs">{erro}</p>
        <button onClick={handleImportar} className="text-xs text-red-500 hover:underline shrink-0 font-semibold">Tentar novamente</button>
      </div>
    )
  }

  if (meta) {
    const tempoAtras = formatDistanceToNow(new Date(meta.importadoEm), { locale: ptBR, addSuffix: true })
    return compact ? (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
        <CheckCircle size={11} className="text-green-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-green-800">{meta.total} pedidos Bling</p>
          <p className="text-[9px] text-green-600">{tempoAtras}</p>
        </div>
        <button onClick={handleImportar} title="Reimportar" className="p-1 rounded hover:bg-green-100 text-green-600">
          <RefreshCw size={10} />
        </button>
        <button onClick={handleLimpar} title="Remover" className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
          <Trash2 size={10} />
        </button>
      </div>
    ) : (
      <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
        <CheckCircle size={14} className="text-green-600 shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-green-800">{meta.total} pedidos importados do Bling</p>
          <p className="text-[10px] text-green-600">{tempoAtras}</p>
        </div>
        <button onClick={handleImportar} title="Reimportar" className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 transition-colors">
          <RefreshCw size={13} />
        </button>
        <button onClick={handleLimpar} title="Remover" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
          <Trash2 size={13} />
        </button>
      </div>
    )
  }

  if (!blingOk) {
    return compact ? (
      <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-[10px] text-slate-400 text-center">Conecte o Bling em <strong>Config</strong> para importar pedidos</p>
      </div>
    ) : (
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
        <Database size={14} className="text-slate-400 shrink-0" />
        <p className="text-xs text-slate-400">Conecte o <strong>Bling</strong> em Config para importar histórico de pedidos</p>
      </div>
    )
  }

  return compact ? (
    <button
      onClick={handleImportar}
      className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-[#CC8800] bg-[#FFF9C2] hover:bg-[#F5C200]/30 border border-[#F5C200]/40 rounded-xl transition-colors"
    >
      <Download size={12} />
      Importar pedidos do Bling
    </button>
  ) : (
    <button
      onClick={handleImportar}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-[#CC8800] bg-[#FFF9C2] hover:bg-[#F5C200]/30 border border-[#F5C200]/40 rounded-xl transition-colors"
    >
      <Download size={14} />
      Importar histórico do Bling
    </button>
  )
}
